// Solicitudes ARCO (LOPDP): Talento Humano atiende los derechos del trabajador
// sobre sus datos personales. Nómina → Configuración → Solicitudes ARCO.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { Campo, Modal, ESTADOS_ARCO, TIPOS_ARCO, fecha, fieldCls, nomRpc, type ArcoRow } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

const chip = (estado: string, vencida: boolean) =>
  vencida ? 'bg-urgente-soft text-urgente'
  : estado === 'atendida' ? 'bg-estable-soft text-estable'
  : estado === 'rechazada' ? 'bg-muted text-muted-foreground'
  : estado === 'en_proceso' ? 'bg-primary/10 text-primary'
  : 'bg-pendiente-soft text-pendiente';

export function SolicitudesArco() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState('todos');
  const { data: rows = [], isFetching } = useQuery<ArcoRow[]>({
    queryKey: ['nom-arco', estado],
    queryFn: () => nomRpc<ArcoRow[]>('nom_arco', { _estado: estado }),
  });

  const [editando, setEditando] = useState<ArcoRow | null>(null);
  const [nuevoEstado, setNuevoEstado] = useState('en_proceso');
  const [respuesta, setRespuesta] = useState('');

  const resolver = useMutation({
    mutationFn: () => nomRpc('nom_arco_resolver', { _data: { id: editando!.id, estado: nuevoEstado, respuesta: respuesta || null } }),
    onSuccess: () => { toast.success('Solicitud actualizada'); setEditando(null); void qc.invalidateQueries({ queryKey: ['nom-arco'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pendientes = rows.filter((r) => r.estado === 'pendiente' || r.estado === 'en_proceso').length;
  const vencidas = rows.filter((r) => r.vencida).length;

  // Exportar (CSV / XLS / PDF) — respeta el filtro actual. Sin columna de montos → sin fila de TOTAL.
  const headersExp = ['N°', 'Fecha', 'Trabajador', 'Derecho', 'Solicitud', 'Límite', 'Estado'];
  const filasExport: Celda[][] = rows.map((r, i): Celda[] => [
    i + 1, fecha(r.created_at), `${r.razon_social} (${r.identificacion})`,
    TIPOS_ARCO[r.tipo] ?? r.tipo, r.descripcion ?? '', fecha(r.fecha_limite),
    r.vencida ? 'Vencida' : ESTADOS_ARCO[r.estado] ?? r.estado,
  ]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Solicitudes ARCO</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-48`} value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Estado">
            <option value="todos">Todos los estados</option>
            {Object.entries(ESTADOS_ARCO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          <ExportarBotones
            nombre={`solicitudes-arco-${fechaLocal()}`} titulo="Solicitudes ARCO"
            headers={headersExp} filas={filasExport} colsDerecha={[0]}
            subtitulo={`${new Date().toLocaleString(LOCALE)} · ${rows.length} solicitud(es)`} disabled={rows.length === 0} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Derechos LOPDP (Arts. 12-17): Acceso, Rectificación, Eliminación, Oposición, Portabilidad y Suspensión.
        Plazo legal de respuesta: 15 días desde la solicitud.
        {pendientes > 0 && <span className="text-pendiente"> · {pendientes} por atender</span>}
        {vencidas > 0 && <span className="text-urgente"> · {vencidas} vencidas</span>}
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Solicitudes ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Trabajador</th><th className="py-2 pr-3">Derecho</th>
              <th className="py-2 pr-3">Solicitud</th><th className="py-2 pr-3">Límite</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin solicitudes</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40 align-top">
                  <td className="py-2 pr-3 font-mono whitespace-nowrap">{fecha(r.created_at)}</td>
                  <td className="py-2 pr-3">{r.razon_social}<div className="text-xs text-muted-foreground font-mono">{r.identificacion}</div></td>
                  <td className="py-2 pr-3">{TIPOS_ARCO[r.tipo] ?? r.tipo}</td>
                  <td className="py-2 pr-3 max-w-xs"><div className="text-muted-foreground">{r.descripcion}</div>{r.respuesta && <div className="text-xs mt-1"><span className="text-muted-foreground">Respuesta: </span>{r.respuesta}</div>}</td>
                  <td className="py-2 pr-3 font-mono whitespace-nowrap">{fecha(r.fecha_limite)}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${chip(r.estado, r.vencida)}`}>
                      {r.vencida ? 'Vencida' : ESTADOS_ARCO[r.estado] ?? r.estado}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    {(r.estado === 'pendiente' || r.estado === 'en_proceso') && (
                      <Button variant="outline" size="sm" onClick={() => { setEditando(r); setNuevoEstado(r.estado === 'pendiente' ? 'en_proceso' : 'atendida'); setRespuesta(r.respuesta ?? ''); }}>Atender</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editando && (
        <Modal title={`Atender solicitud — ${TIPOS_ARCO[editando.tipo] ?? editando.tipo}`} onClose={() => setEditando(null)}>
          <div className="space-y-3">
            <div className="text-sm"><span className="text-muted-foreground">Trabajador: </span>{editando.razon_social}</div>
            <div className="text-sm bg-muted/40 rounded-md p-3">{editando.descripcion}</div>
            <Campo label="Estado">
              <select className={fieldCls} value={nuevoEstado} onChange={(e) => setNuevoEstado(e.target.value)}>
                <option value="en_proceso">En proceso</option>
                <option value="atendida">Atendida</option>
                <option value="rechazada">Rechazada</option>
              </select>
            </Campo>
            <Campo label="Respuesta al trabajador">
              <textarea className={`${fieldCls} min-h-[90px]`} value={respuesta} onChange={(e) => setRespuesta(e.target.value)}
                placeholder="Detalle de la gestión (qué se hizo, qué datos se entregaron/rectificaron, o el motivo del rechazo)" />
            </Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditando(null)}>Cancelar</Button>
              <Button isLoading={resolver.isPending} onClick={() => resolver.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
